'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@/comps/primitives/button';
import { InfoTooltip } from '@/comps/info-tooltip';
import { AiProficiencyReportCard } from '@/domains/pages/ai-proficiency/components/ai-proficiency-report-card';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import { RecommendedMixLabel } from '@/domains/pages/model-savings/components/recommended-mix-label';
import { ShareOfTotalLabel } from '@/domains/pages/model-savings/components/share-of-total-label';
import { SavingsShareProgress } from '@/domains/pages/model-savings/components/savings-share-progress';
import { MODEL_SAVINGS_POSITIVE_COLOR, MODEL_SAVINGS_ROW_SUFFIX_CLASS, SAVINGS_OPPORTUNITIES_EXPLANATION } from '@/domains/pages/model-savings/constants';
import type { SavingsTask } from '@/domains/pages/model-savings/types';
import { computeTasksSavingShareOfSpendPercent, formatAnnualSavings, formatAnnualSpend, sumTasksSavingAnnual } from '@/domains/pages/model-savings/utils/model-savings.utils';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { ArrowRight } from 'lucide-react';
import { useMemo } from 'react';
const TOP_OPPORTUNITIES_METHOD_TOOLTIP = 'Each card shows a task that could use lower-cost model tiers while maintaining similar output quality. Savings are the annualized difference between AI spend on the current model mix and the recommended mix, based on list model prices.';
type TopOpportunitiesCardProps = {
  tasks: SavingsTask[];
  totalSpendAnnual: number;
  onSeeAll: () => void;
  onTaskClick: (taskId: string) => void;
};
export function TopOpportunitiesCard(t0) {
  const $ = _c(32);
  const {
    tasks,
    totalSpendAnnual,
    onSeeAll,
    onTaskClick
  } = t0;
  let t1;
  if ($[0] !== tasks || $[1] !== totalSpendAnnual) {
    t1 = computeTasksSavingShareOfSpendPercent(tasks, totalSpendAnnual);
    $[0] = tasks;
    $[1] = totalSpendAnnual;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const topTasksSharePercent = t1;
  let t2;
  if ($[3] !== tasks) {
    t2 = sumTasksSavingAnnual(tasks);
    $[3] = tasks;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const topTasksSavingAnnual = t2;
  const t3 = `Top ${tasks.length} opportunity saving potentials`;
  const t4 = tasks.length;
  let t5;
  if ($[5] !== topTasksSavingAnnual) {
    t5 = formatAnnualSavings(topTasksSavingAnnual);
    $[5] = topTasksSavingAnnual;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== topTasksSharePercent) {
    t6 = formatScore(topTasksSharePercent, {
      decimals: 0
    });
    $[7] = topTasksSharePercent;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== totalSpendAnnual) {
    t7 = formatAnnualSpend(totalSpendAnnual);
    $[9] = totalSpendAnnual;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = `Switching these ${t4} tasks to their recommended model tier mix could save ${t5} per year, equal to ${t6}% of your ${t7} annualized AI spend.`;
  let t9;
  if ($[11] !== t3 || $[12] !== t8 || $[13] !== topTasksSharePercent) {
    t9 = <ShareOfTotalLabel className="hidden sm:flex" percent={topTasksSharePercent} label={t3} tooltip={t8} />;
    $[11] = t3;
    $[12] = t8;
    $[13] = topTasksSharePercent;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ArrowRight className="size-3" />;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== onSeeAll) {
    t11 = <Button variant="outline" size="sm" className="h-7 gap-1.5 rounded-full px-3 text-xs text-muted-foreground hover:text-foreground" onClick={onSeeAll}>All opportunities{t10}</Button>;
    $[16] = onSeeAll;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t9) {
    t12 = <div className="flex items-center gap-3">{t9}{t11}</div>;
    $[18] = t11;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <p className="shrink-0 border-b border-b-lightborder px-8 py-2.5 text-sm leading-snug text-muted-foreground">{SAVINGS_OPPORTUNITIES_EXPLANATION}<InfoTooltip className="inline-flex align-middle" content={TOP_OPPORTUNITIES_METHOD_TOOLTIP} label="How the recommended mix and its saving are worked out" /></p>;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== onTaskClick || $[23] !== tasks) {
    let t15;
    if ($[25] !== onTaskClick) {
      t15 = task => <OpportunityTaskCard key={task.id} task={task} onClick={() => onTaskClick(task.id)} />;
      $[25] = onTaskClick;
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    t14 = tasks.map(t15);
    $[22] = onTaskClick;
    $[23] = tasks;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[27] !== t14) {
    t15 = <div className="flex min-h-0 flex-1 flex-col divide-y divide-lightborder">{t14}</div>;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t12 || $[30] !== t15) {
    t16 = <AiProficiencyReportCard title="Top savings opportunities" description="The tasks with the largest annualized savings if each used the most cost-efficient model tier." className="min-h-0 flex-1" contentClassName="flex min-h-0 flex-1 flex-col p-0!" headerTrailing={t12}>{t13}{t15}</AiProficiencyReportCard>;
    $[29] = t12;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  return t16;
}

/** One opportunity as a full-width panel, matching the level cards on the upskilling page. */
function OpportunityTaskCard(t0) {
  const $ = _c(30);
  const {
    task,
    onClick
  } = t0;
  let t1;
  if ($[0] !== task.name) {
    t1 = <p className="line-clamp-1 min-w-0 text-sm font-medium text-foreground">{task.name}</p>;
    $[0] = task.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("text-lg leading-none font-medium tracking-tight", AIFI_DATA_NUMBER_CLASS);
    t3 = {
      color: MODEL_SAVINGS_POSITIVE_COLOR
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== task.savingAnnual) {
    t4 = formatAnnualSavings(task.savingAnnual);
    $[4] = task.savingAnnual;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className={MODEL_SAVINGS_ROW_SUFFIX_CLASS}>/Y</span>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4) {
    t6 = <span className={t2} style={t3}>{t4}{t5}</span>;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== task.savingSharePercent) {
    t7 = <SavingsShareProgress value={task.savingSharePercent} size={18} />;
    $[9] = task.savingSharePercent;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <div className="flex shrink-0 items-center gap-2">{t6}{t7}</div>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t1 || $[15] !== t8) {
    t9 = <div className="flex w-full items-center justify-between gap-4">{t1}{t8}</div>;
    $[14] = t1;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== task.recommendedMix) {
    t10 = <RecommendedMixLabel mix={task.recommendedMix} className="min-w-0" />;
    $[17] = task.recommendedMix;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== task.spendAnnual) {
    t11 = formatAnnualSpend(task.spendAnnual);
    $[19] = task.spendAnnual;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11) {
    t12 = <p className="shrink-0 text-xs text-nowrap text-muted-foreground">of <span className={AIFI_DATA_NUMBER_CLASS}>{t11}</span> / Y actual AI spend</p>;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t12) {
    t13 = <div className="flex w-full items-center justify-between gap-4">{t10}{t12}</div>;
    $[23] = t10;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== onClick || $[27] !== t13 || $[28] !== t9) {
    t14 = <button type="button" onClick={onClick} className="flex w-full min-w-0 flex-1 flex-col justify-center gap-1.5 px-8 py-3 text-left transition-colors hover:bg-muted/20">{t9}{t13}</button>;
    $[26] = onClick;
    $[27] = t13;
    $[28] = t9;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}